import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { PageTransition } from "@/components/store/PageTransition";
import { AppProvider } from "@/contexts/AppContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { TransitionLoader } from "@/components/TransitionLoader";
import { LanguageLoader } from "@/components/LanguageLoader";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import OrderVerify from "@/pages/OrderVerify";
import Favorites from "@/pages/Favorites";
import Auth from "@/pages/Auth";
import Profile from "@/pages/Profile";
import ReturnsPolicy from "@/pages/ReturnsPolicy";
import NotFound from "@/pages/NotFound";
import { RequireAuth, RequireAdmin } from "@/components/guards";

import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminAppearance from "@/pages/admin/AdminAppearance";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminNewArrivals from "@/pages/admin/AdminNewArrivals";
import AdminHomeCollections from "@/pages/admin/AdminCollections";
import AdminProductColors from "@/pages/admin/AdminProductColors";
import AdminBuyItWith from "@/pages/admin/AdminBuyItWith";
import AdminSizeGuides from "@/pages/admin/AdminSizeGuides";
import AdminSounds from "@/pages/admin/AdminSounds";

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <PageTransition key={location.pathname}>
      <Routes location={location}>
        {/* Site Layout Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/returns" element={<ReturnsPolicy />} />
        <Route path="/terms" element={<ReturnsPolicy />} />

        {/* Store Layout Routes */}
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order/verify" element={<OrderVerify />} />
        <Route path="/order-verify" element={<OrderVerify />} />
        <Route path="/order-success" element={<RequireAuth><OrderSuccess /></RequireAuth>} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />

        <Route path="*" element={<NotFound />} />

        {/* Auth Route */}
        <Route path="/auth" element={<Auth />} />

        {/* Admin Dashboard */}
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminOverview />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="subcategories" element={<AdminCategories />} />
          <Route path="new-arrivals" element={<AdminNewArrivals />} />
          <Route path="collections" element={<AdminHomeCollections />} />
          <Route path="product-colors" element={<AdminProductColors />} />
          <Route path="buy-it-with" element={<AdminBuyItWith />} />
          <Route path="size-guides" element={<AdminSizeGuides />} />
          <Route path="appearance" element={<AdminAppearance />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="shipping" element={<AdminShipping />} />
          <Route path="sounds" element={<AdminSounds />} />
        </Route>
      </Routes>
    </PageTransition>
  );
}

import { VisitorTracker } from "@/components/VisitorTracker";

export default function App() {
  return (
    <AppProvider>
      <Toaster />
      <ScrollToTop />
      <TransitionLoader />
      <LanguageLoader />
      <VisitorTracker />
      <AnimatedRoutes />
    </AppProvider>
  );
}
